import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

/**
 * Hook for fetching company-related data
 */
export const useCompanyData = (companyId) => {
  const [employees, setEmployees] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch company employees for speaker selection
   */
  const fetchCompanyEmployees = useCallback(async (companyIdParam) => {
    const idToUse = companyIdParam || companyId;
    if (!idToUse) return [];
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query SearchEmployees(
            $filter: SearchableEmployeeFilterInput
            $sort: [SearchableEmployeeSortInput]
            $limit: Int
            $nextToken: String
          ) {
            searchEmployees(
              filter: $filter
              sort: $sort
              limit: $limit
              nextToken: $nextToken
            ) {
              items {
                id
                user {
                  id
                  firstName
                  lastName
                  middleName
                  title
                  email
                }
                department {
                  items {
                    department {
                      name {
                        language
                        text
                      }
                    }
                  }
                }
                role
                status
                image {
                  fileName
                  alt
                }
              }
              nextToken
              total
            }
          }
        `,
        variables: {
          filter: {
            companyEmployeesId: { eq: idToUse },
            status: { eq: 'active' }
          },
          limit: 5000
        },
      });
      
      const employeesList = response.data.searchEmployees.items || [];
      setEmployees(employeesList);
      return employeesList;
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError(error.message || 'Failed to fetch employees');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  /**
   * Fetch company details
   */
  const fetchCompanyDetails = useCallback(async (companyIdParam) => {
    const idToUse = companyIdParam || companyId;
    if (!idToUse) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query GetCompany($id: ID!) {
            getCompany(id: $id) {
              id
              name
              industry
              status
              website
              description {
                language
                text
              }
              image {
                main {
                  fileName
                  alt
                }
                logo {
                  fileName
                  alt
                }
              }
              contact {
                phone
                mobile
                fax
                email
                address {
                  street
                  streetNumber
                  city
                  zip
                  countryCode
                }
              }
              admins
              
              x4AdminId
              companyAdminId
            }
          }
        `,
        variables: { id: idToUse }
      });
      
      const company = response.data.getCompany;
      setCompanyDetails(company);
      return company;
    } catch (error) {
      console.error('Error fetching company details:', error);
      setError(error.message || 'Failed to fetch company details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  /**
   * Search employees by name
   */
  const searchEmployees = useCallback((searchTerm) => {
    if (!searchTerm) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return employees.filter((employee) => {
      const fullName = `${employee.user?.firstName || ''} ${employee.user?.middleName || ''} ${employee.user?.lastName || ''}`.toLowerCase();
      const email = employee.user?.email?.toLowerCase() || '';
      
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [employees]);

  /**
   * Get employee by ID
   */
  const getEmployeeById = useCallback((employeeId) => {
    return employees.find(emp => emp.id === employeeId);
  }, [employees]);

  /**
   * Transform employee to speaker format for the form
   */
  const transformEmployeeToSpeaker = useCallback((employee, type = 'co_speaker') => {
    if (!employee) return null;
    
    // Get department name from nested structure
    const departmentName = employee.department?.items?.[0]?.department?.name?.[0]?.text || '';
    
    return {
      id: employee.id,
      employeeId: employee.id,
      firstName: employee.user?.firstName || '',
      lastName: employee.user?.lastName || '',
      middleName: employee.user?.middleName || '',
      title: employee.user?.title || '',
      department: departmentName,
      image: employee.image,
      type: type
    };
  }, []);

  /**
   * Auto-fetch employees when companyId changes
   */
  useEffect(() => {
    if (companyId) {
      fetchCompanyEmployees();
    }
  }, [companyId, fetchCompanyEmployees]);

  /**
   * Reload employees - wrapper around fetchCompanyEmployees for convenience
   */
  const reloadEmployees = useCallback(() => {
    return fetchCompanyEmployees();
  }, [fetchCompanyEmployees]);

  return {
    employees,
    companyDetails,
    isLoading,
    error,
    fetchCompanyEmployees,
    fetchCompanyDetails,
    searchEmployees,
    getEmployeeById,
    transformEmployeeToSpeaker,
    reloadEmployees
  };
};